import type { ParsedUrlQuery } from "querystring";

import { SEARCH_ROUTE_PARAM } from "@/features/search/api/route";
import type { PageInfo } from "@/features/search/api/types";

/**
 * Whether a listing URL carries anything in its query string besides `page`.
 *
 * `?page=N` is the paginated set and stays indexable (each page self-canonicalises, see
 * `SearchSeo`). Everything else, sorts, facets, price bounds, tracking tags, is a view of the same
 * set that the canonical already collapses onto the plain URL. Search Console (2026-09-15)
 * showed those parameter URLs crawled and reported as duplicates at scale, so they are marked
 * `noindex,follow` as well: out of the index, links still counted.
 *
 * The catch-all segment and Next's own rewrite params are routing, not query string.
 */
export const hasNonPaginationParams = (query: ParsedUrlQuery) =>
  Object.entries(query).some(
    ([key, value]) =>
      key !== SEARCH_ROUTE_PARAM &&
      key !== "page" &&
      key !== "nextInternalLocale" &&
      !key.startsWith("nxtP") &&
      value != null &&
      value !== ""
  );

/**
 * Whether the service found nothing live for a listing URL.
 *
 * The sitemap generator (whoppah/sitemaps #6) already drops pages with zero live products, but
 * the page itself still answered 200 and indexable, so crawlers kept reaching it through links
 * and Search Console (2026-09-15) filed 23,710 URLs as soft 404. Such a page is `noindex,follow`:
 * it still renders its empty state with a 200, the sitemap and the page agree, and its links
 * keep counting. Only an explicit zero counts: a missing response (a failed search) is unknown,
 * not empty, so a transient outage never takes a good page out of the index.
 */
export const isEmptyListing = (
  pageInfo: Pick<PageInfo, "total"> | null | undefined
) => pageInfo?.total === 0;
